import React, { useEffect, useMemo, useRef, useState } from "react";
import { Flag } from "@/utils/flag";
import { ChevronsUpDown, Search, Check } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { PhoneCountryBottomSheet } from "@/components/mobile/PhoneCountryBottomSheet";
import { Capacitor } from "@capacitor/core";
import { COUNTRIES } from "@/utils/countries";

interface PhoneCountrySelectProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const PhoneCountrySelect = React.memo(({ value, onChange, disabled }: PhoneCountrySelectProps) => {
  const isNative = Capacitor.isNativePlatform();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!dropdownOpen) {
      setSearchQuery("");
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.clearTimeout(focusTimer);
    };
  }, [dropdownOpen]);

  const filteredCountries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return COUNTRIES;

    return COUNTRIES.filter((country) => (
      country.label.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query) ||
      country.dialCode.includes(searchQuery.trim())
    ));
  }, [searchQuery]);

  if (isNative) {
    return (
      <>
        <button type="button" disabled={disabled} onClick={() => setSheetOpen(true)} className="flex items-center gap-1.5 px-0 py-1 hover:opacity-70 transition-opacity bg-transparent border-0 focus:outline-none mr-2">
          <Flag code={value || "GB"} size={18} />
          <ChevronsUpDown className="h-3 w-3 text-gold/40" />
        </button>
        <PhoneCountryBottomSheet
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          value={value || "GB"}
          onChange={(code) => { onChange(code); setSheetOpen(false); }}
        />
      </>
    );
  }

  return (
    <div ref={wrapperRef} className="relative mr-2">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setDropdownOpen((open) => !open)}
        className="flex items-center gap-1.5 px-0 py-1 hover:opacity-70 transition-opacity bg-transparent border-0 focus:outline-none"
      >
        <Flag code={value || "GB"} size={18} />
        <ChevronsUpDown className="h-3 w-3 text-gold/40" />
      </button>

      {dropdownOpen && (
        <div
          className="absolute left-0 top-full mt-2 w-[min(calc(100vw-32px),320px)] overflow-hidden rounded-md border border-gold/20 bg-background shadow-2xl shadow-black/60 z-[99999]"
          style={{ maxWidth: "calc(100vw - 32px)" }}
        >
          <Command className="bg-transparent" shouldFilter={false}>
            <div className="flex items-center border-b border-gold/10 px-3">
              <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-gold/40" />
              <CommandInput
                ref={inputRef}
                value={searchQuery}
                onValueChange={setSearchQuery}
                placeholder="Search country…"
                className="h-10 bg-transparent border-0 text-ivory placeholder:text-ivory/30 focus:ring-0 text-sm"
              />
            </div>
            <CommandList className="overflow-y-auto overscroll-contain max-h-[256px]">
              <CommandEmpty className="py-8 text-center text-sm text-ivory/40">
                No country found.
              </CommandEmpty>
              <CommandGroup className="p-1">
                {filteredCountries.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={country.code}
                    onSelect={() => {
                      onChange(country.code);
                      setDropdownOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gold/10 text-ivory data-[selected=true]:bg-gold/15 data-[selected=true]:text-ivory transition-colors"
                  >
                    <Flag code={country.code} size={18} />
                    <div className="flex-1 flex items-center justify-between min-w-0">
                      <span className="text-sm truncate">{country.label}</span>
                      <span className="text-xs text-gold/50 font-mono ml-2 flex-shrink-0">{country.dialCode}</span>
                    </div>
                    <Check className={`h-3.5 w-3.5 text-gold transition-opacity ${value === country.code ? "opacity-100" : "opacity-0"}`} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
});

PhoneCountrySelect.displayName = "PhoneCountrySelect";
